import {
  supabase,
  type IntegrationPartner,
  type IntegrationPartnerEndpoint,
} from './supabase';

/**
 * Integration Service
 * Fetches integration partner data and endpoints using Supabase authentication
 */

export interface IntegrationPartnerWithEndpoints extends IntegrationPartner {
  endpoints: IntegrationPartnerEndpoint[];
}

export interface FetchIntegrationDataResult {
  success: boolean;
  error?: string;
  partner?: IntegrationPartnerWithEndpoints;
}

// Simple cache to prevent duplicate API calls
const integrationCache = new Map<
  string,
  {
    data: IntegrationPartnerWithEndpoints;
    timestamp: number;
  }
>();

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const integrationService = {
  /**
   * Fetch integration partner data and endpoints by partner ID
   * Includes caching to prevent duplicate API calls
   */
  async fetchIntegrationPartnerById(
    partnerId: string
  ): Promise<FetchIntegrationDataResult> {
    try {
      // Check cache first
      const cached = integrationCache.get(partnerId);
      const now = Date.now();

      if (cached && now - cached.timestamp < CACHE_DURATION) {
        console.log(
          '✅ IntegrationService: Using cached data for partner:',
          partnerId
        );
        return {
          success: true,
          partner: cached.data,
        };
      }

      console.log(
        '🔄 IntegrationService: Fetching fresh data for partner:',
        partnerId
      );

      // Fetch integration partner data
      const { data: partner, error: partnerError } = await supabase
        .from('ev_integration_partners')
        .select('*')
        .eq('id', partnerId)
        .eq('is_active', true)
        .single();

      if (partnerError) {
        return {
          success: false,
          error: `Partner error: ${partnerError.message}`,
        };
      }

      if (!partner) {
        return {
          success: false,
          error: 'Integration partner not found or inactive',
        };
      }

      // Fetch integration partner endpoints
      const { data: endpoints, error: endpointsError } = await supabase
        .from('ev_integration_partner_endpoints')
        .select(
          `
          *,
          integration_endpoint:ev_integration_endpoints (
            id,
            action,
            description,
            method,
            created_at,
            updated_at
          )
        `
        )
        .eq('ev_integration_partner_id', partner.id)
        .eq('is_active', true)
        .order('created_at', { ascending: true });

      if (endpointsError) {
        return {
          success: false,
          error: `Endpoints error: ${endpointsError.message}`,
        };
      }

      // Combine partner data with endpoints
      const partnerWithEndpoints: IntegrationPartnerWithEndpoints = {
        ...partner,
        endpoints: endpoints || [],
      };

      // Cache the result
      integrationCache.set(partnerId, {
        data: partnerWithEndpoints,
        timestamp: now,
      });

      console.log(
        '✅ IntegrationService: Data fetched and cached for partner:',
        partnerId
      );

      return {
        success: true,
        partner: partnerWithEndpoints,
      };
    } catch (error) {
      return {
        success: false,
        error: `Unexpected error: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      };
    }
  },

  /**
   * Clear cache for a specific partner or all partners
   */
  clearCache(partnerId?: string): void {
    if (partnerId) {
      integrationCache.delete(partnerId);
      console.log(
        '🗑️ IntegrationService: Cleared cache for partner:',
        partnerId
      );
    } else {
      integrationCache.clear();
      console.log('🗑️ IntegrationService: Cleared all cache');
    }
  },

  /**
   * Get endpoint URL for a specific action and environment
   */
  async getEndpointUrl(
    partnerId: string,
    action: string,
    environment: 'development' | 'staging' | 'production' = 'production'
  ): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      const { data: endpoint, error } = await supabase
        .from('ev_integration_partner_endpoints')
        .select(
          `
          endpoint_url,
          ev_integration_endpoints!inner (
            action
          )
        `
        )
        .eq('ev_integration_partner_id', partnerId)
        .eq('ev_integration_endpoints.action', action)
        .eq('environment', environment)
        .eq('is_active', true)
        .single();

      if (error) {
        return {
          success: false,
          error: `Endpoint not found: ${error.message}`,
        };
      }

      return {
        success: true,
        url: endpoint.endpoint_url,
      };
    } catch (error) {
      return {
        success: false,
        error: `Unexpected error: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      };
    }
  },
};
